import type { WorkspaceRoleKey } from 'hooks/globalApi';
import { useToast } from 'components/ToastManager';
import {
  useDeleteWorkspaceMemberMutation as useDeleteWorkspaceMemberMutationGlobal,
  useUpdateWorkspaceMemberMutation,
} from 'hooks/globalApi';
import { useGlobalApiContext } from 'hooks/useGlobalApiContext';
import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import { useUserInfo } from 'hooks/useUserInfo';
import { useWorkspaceId } from 'hooks/useWorkspace';
import React, { useCallback, useEffect, useState } from 'react';
import { useCatchMutationError } from 'utils/urql';
import {
  workspaceRoleLabels,
  workspaceRoleOptions,
} from 'utils/workspaceRoles';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Toast } from '@fragment/ui/src/components/Toast';

type MemberDetailRow = {
  id: string;
  email: string;
  role: WorkspaceRoleKey;
  firstName?: string | null;
  lastName?: string | null;
};

type Params = {
  row: MemberDetailRow;
  onDelete: () => void;
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center">
    <span className="text-main-500 w-[14ch] shrink-0">{label}</span>
    <span className="flex-1 text-main">{children}</span>
  </div>
);

export const MemberDetail = ({ row, onDelete }: Params) => {
  const workspaceId = useWorkspaceId();
  const { context } = useGlobalApiContext();
  const { showToast } = useToast();
  const { data: user } = useUserInfo();

  const [role, setRole] = useState(row.role);
  const [confirming, setConfirming] = useState(false);

  const canUpdate = useHasWorkspacePermission(
    Permissions.workspace_member.update
  );
  const canDelete = useHasWorkspacePermission(
    Permissions.workspace_member.delete
  );
  const isSelf = row.id === user?.id;

  const name =
    `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || row.email;
  const initial = (row.firstName || row.email).charAt(0).toUpperCase();

  const [{ fetching: isUpdatingRole }, updateWorkspaceMember] =
    useCatchMutationError(
      useUpdateWorkspaceMemberMutation,
      'updateWorkspaceMember'
    );
  const [{ fetching: isDeleting, error: deleteError }, deleteWorkspaceMember] =
    useCatchMutationError(
      useDeleteWorkspaceMemberMutationGlobal,
      'deleteMemberFromWorkspace'
    );

  const handleRoleChange = useCallback(
    (nextRole: string): void => {
      const parsed = nextRole as WorkspaceRoleKey;
      if (parsed === role || isUpdatingRole) {
        return;
      }
      const previous = role;
      setRole(parsed);
      const showError = () => {
        setRole(previous);
        showToast(
          <Toast type="error" message="Error updating role, try again." />
        );
      };
      const run = async (): Promise<void> => {
        const { error } = await updateWorkspaceMember(
          { workspaceId, userId: row.id, role: parsed },
          context
        );
        if (error) {
          showError();
        } else {
          showToast(<Toast type="success" message="Role updated" />);
        }
      };
      run().catch(showError);
    },
    [
      role,
      isUpdatingRole,
      updateWorkspaceMember,
      workspaceId,
      row.id,
      context,
      showToast,
    ]
  );

  const submitRemoveMember = useCallback(async () => {
    const { error } = await deleteWorkspaceMember(
      { workspaceId, userId: row.id },
      context
    );
    if (!error) {
      onDelete();
      showToast(<Toast type="success" message="Member removed" />);
    } else {
      showToast(
        <Toast type="error" message="Error removing member, try again." />
      );
    }
  }, [
    deleteWorkspaceMember,
    workspaceId,
    row.id,
    context,
    onDelete,
    showToast,
  ]);

  useEffect(() => {
    if (deleteError) {
      showToast(
        <Toast type="error" message="Error removing member, try again." />
      );
    }
  }, [deleteError, showToast]);

  const roleAsText = confirming || isSelf || !canUpdate;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-center h-f2 w-f2 bg-main text-negative">
        {initial}
      </div>
      <div className="flex flex-col gap-f0 mt-f3">
        <Field label="Name">{name}</Field>
        <Field label="Email">{row.email}</Field>
        <Field label="Role">
          {roleAsText ? (
            <span>{workspaceRoleLabels[role]}</span>
          ) : (
            <Dropdown
              displayValue={role}
              onChange={handleRoleChange}
              options={workspaceRoleOptions}
              disabled={isUpdatingRole}
              size="md"
              width="w-fit"
              data-testid="member-role-dropdown"
            />
          )}
        </Field>
      </div>

      {canDelete && (
        <div className="mt-auto">
          {confirming ? (
            <div className="space-y-f2">
              <p>{`Are you sure you wish to remove ${name}?`}</p>
              <FormButtons
                type="tertiary"
                onCancel={() => {
                  setConfirming(false);
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
                onSubmit={submitRemoveMember}
                cancelButtonLabel="Cancel"
                submitButtonLabel={isDeleting ? 'Removing' : 'Yes, Remove'}
                disabled={isSelf}
              />
            </div>
          ) : (
            <PrimaryButtonV2
              disabled={isSelf}
              onClick={() => {
                setConfirming(true);
              }}
            >
              Remove
            </PrimaryButtonV2>
          )}
        </div>
      )}
    </div>
  );
};
